import { Anchor, Box, Group, Loader, Pagination, Select, Stack, Text } from "@mantine/core";
import { IIssueApi } from "@schemas/issueSchema";
import { ColDef, GridReadyEvent, SortChangedEvent } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { AgGridReact } from "ag-grid-react";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { PostProjectByProjectIdIssuesOnBoardsApiResponse } from "../../../../../openapi";
import { AG_GRID_LOCALE_NOR } from "../../../../agGridLocale";
import { OptionBadge } from "../../../../components";
import { UserCellRenderer } from "../../../../components/ConnectedIssues/ConnectedIssues";
import { addAppModalThunk } from "../../../../global/globalSlice";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { AppModalEnums } from "../../../../mc-shared/sharedConstants";
import { formatShortDate } from "../../admin/selectors";
import { IssueIdWithAksjonCompletedCount } from "../IssueIdWithAksjonCompletedCount";
import { ISSUE_STATES_OPTIONS } from "../issueConstants";
import {
  selectIssueBoardTablePnsItems,
  selectMultiRegisterIssuesPage,
  selectMultiRegisterIssuesPageSize,
  selectMultiRegisterIssuesSort,
} from "./multiRegisterIssuesSelectors";
import {
  IMultiRegisterIssuesSort,
  MultiRegisterIssuesSortField,
  setMultiRegisterIssuesPageInRedux,
  setMultiRegisterIssuesPageSizeInRedux,
  setMultiRegisterIssuesSortInRedux,
} from "./multiRegisterIssuesSlice";
import { convertValueAndColorToMultiDisplayColor } from "../issueUtils";

type MultiRegisterIssue = PostProjectByProjectIdIssuesOnBoardsApiResponse["items"][number];

const PAGE_SIZE_OPTIONS = ["100", "250", "500"];
const SORTABLE_FIELDS: MultiRegisterIssuesSortField[] = [
  "issueBoard.name",
  "id",
  "title",
  "status",
  "assignedTo",
  "dates.due",
  "pns",
];

const getSortFromGrid = (
  event: GridReadyEvent<MultiRegisterIssue> | SortChangedEvent<MultiRegisterIssue>,
  preferredColId?: string,
) => {
  const sortedColumns = event.api.getColumnState().filter((column) => column.sort === "asc" || column.sort === "desc");
  const sortedColumn = sortedColumns.find((column) => column.colId === preferredColId) ?? sortedColumns[0];
  if (sortedColumn == null || SORTABLE_FIELDS.includes(sortedColumn.colId as MultiRegisterIssuesSortField) === false) {
    return null;
  }

  return {
    field: sortedColumn.colId as MultiRegisterIssuesSortField,
    direction: sortedColumn.sort,
  } satisfies IMultiRegisterIssuesSort;
};

const MultiRegisterIssuesTable: React.FC<{
  issues: MultiRegisterIssue[];
  projectId: string;
  totalCount: number;
  isLoading: boolean;
}> = ({ issues, projectId, totalCount, isLoading }) => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<AgGridReact<MultiRegisterIssue>>(null);
  const page = useAppSelector(selectMultiRegisterIssuesPage);
  const pageSize = useAppSelector(selectMultiRegisterIssuesPageSize);
  const pnsItems = useAppSelector(selectIssueBoardTablePnsItems);
  const activeSort = useAppSelector(selectMultiRegisterIssuesSort);
  const isApplyingSortStateRef = useRef(false);
  const pageCount = Math.max(Math.ceil(totalCount / pageSize), 1);

  const openIssue = useCallback(
    (issue: MultiRegisterIssue) => {
      dispatch(
        addAppModalThunk({
          app: AppModalEnums.ISSUE,
          itemId: issue._id,
          boardId: issue.issueBoard._id,
          projectId,
        }),
      );
    },
    [dispatch, projectId],
  );

  const columnDefs: ColDef<MultiRegisterIssue>[] = useMemo(
    () => [
      {
        headerName: "Register",
        field: "issueBoard.name",
        minWidth: 180,
        flex: 1,
        cellRenderer: (params) => (
          <Anchor href={`/#/project/${projectId}/issues/${params.data.issueBoard._id}`} fw={500}>
            {params.value}
          </Anchor>
        ),
      },
      {
        headerName: "ID",
        field: "id",
        minWidth: 140,
        width: 140,
        cellClass: "grid-display-block px-1",
        cellRenderer: (params) => (
          <IssueIdWithAksjonCompletedCount
            id={params.value}
            issue={params.data as unknown as IIssueApi}
            setSelectedIssue={() => openIssue(params.data)}
          />
        ),
      },
      {
        headerName: "Tittel",
        field: "title",
        minWidth: 260,
        flex: 2,
        cellRenderer: (params) => <Text fw={500}>{params.value}</Text>,
      },
      {
        headerName: "Status",
        field: "status",
        minWidth: 130,
        cellRenderer: (params) => (
          <OptionBadge
            activeOption={params.value}
            options={convertValueAndColorToMultiDisplayColor(ISSUE_STATES_OPTIONS)}
          />
        ),
      },
      {
        headerName: "Ansvarlig",
        field: "assignedTo",
        minWidth: 150,
        valueGetter: (params) => params.data?.assignedTo?.name ?? "",
        cellRenderer: (params) => <UserCellRenderer height="18px" user={params.data?.assignedTo} />,
      },
      {
        headerName: "Frist",
        field: "dates.due",
        minWidth: 120,
        valueGetter: (params) => params.data?.dates?.due ?? null,
        cellRenderer: (params) => (params.value ? formatShortDate(params.value, true) : ""),
      },
      {
        headerName: "PNS",
        field: "pns",
        minWidth: 110,
        cellRenderer: (params) => {
          const pnsItem = pnsItems?.find((pns) => pns.props?.pnsId === params.value);
          if (pnsItem != null) {
            return `${pnsItem.props?.pnsId} - ${pnsItem.props?.title}`;
          } else {
            return params.value;
          }
        },
      },
    ],
    [openIssue, pnsItems],
  );

  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api) return;

    if (isLoading) {
      api.showLoadingOverlay();
    } else {
      api.hideOverlay();
    }
  }, [isLoading]);

  const handleGridReady = useCallback(
    (event: GridReadyEvent<MultiRegisterIssue>) => {
      if (activeSort != null) {
        isApplyingSortStateRef.current = true;
        event.api.applyColumnState({
          state: [{ colId: activeSort.field, sort: activeSort.direction }],
          defaultState: { sort: null },
        });
        isApplyingSortStateRef.current = false;
      }
      if (isLoading) {
        event.api.showLoadingOverlay();
      }
    },
    [activeSort, isLoading],
  );

  const handleSortChanged = useCallback(
    (event: SortChangedEvent<MultiRegisterIssue>) => {
      if (isApplyingSortStateRef.current) return;

      const sortedColumns = event.api
        .getColumnState()
        .filter((column) => column.sort === "asc" || column.sort === "desc");
      const changedSortedColumnId = event.columns
        ?.map((column) => column.getColId())
        .find((colId) => sortedColumns.some((column) => column.colId === colId));
      const sort = getSortFromGrid(event, changedSortedColumnId);

      if (sort != null && sortedColumns.length > 1) {
        isApplyingSortStateRef.current = true;
        event.api.applyColumnState({
          state: [{ colId: sort.field, sort: sort.direction }],
          defaultState: { sort: null },
        });
        isApplyingSortStateRef.current = false;
      }

      dispatch(setMultiRegisterIssuesSortInRedux(sort));
    },
    [dispatch],
  );

  return (
    <Stack gap="md" flex={1} style={{ minHeight: 0 }}>
      <Box pos="relative" flex={1} style={{ minHeight: 0 }}>
        {isLoading && (
          <Box
            pos="absolute"
            inset={0}
            style={{
              zIndex: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(255, 255, 255, 0.55)",
            }}
          >
            <Loader size="lg" />
          </Box>
        )}
        <Box className="ag-theme-quartz" h="100%" w="100%">
          <AgGridReact
            ref={gridRef}
            rowData={issues}
            columnDefs={columnDefs}
            defaultColDef={{
              sortable: true,
              resizable: true,
              suppressHeaderMenuButton: true,
            }}
            rowHeight={42}
            animateRows
            suppressContextMenu
            localeText={AG_GRID_LOCALE_NOR}
            getRowId={(params) => params.data._id}
            onGridReady={handleGridReady}
            suppressMultiSort
            onSortChanged={handleSortChanged}
          />
        </Box>
      </Box>
      <Group justify="space-between" flex="0 0 auto">
        <Text size="sm" c="dimmed">
          Viser {issues.length} av {totalCount} saker
        </Text>
        <Group gap="sm">
          <Select
            size="xs"
            w={110}
            value={String(pageSize)}
            data={PAGE_SIZE_OPTIONS}
            onChange={(value) => dispatch(setMultiRegisterIssuesPageSizeInRedux(Number(value ?? pageSize)))}
          />
          <Pagination
            size="sm"
            total={pageCount}
            value={page}
            onChange={(nextPage) => dispatch(setMultiRegisterIssuesPageInRedux(nextPage))}
          />
        </Group>
      </Group>
    </Stack>
  );
};

export default MultiRegisterIssuesTable;
